import { DatePicker } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'

import { genderOptions } from 'sections/LKSection/modules/UserModule/data'
import { CompanyDetailsViewModel } from 'sections/LKSection/modules/UserModule/stores/models/CompanyDetailsViewModel'
import { Citizenship, DocumentType, GENDER, UserModuleKeys } from 'sections/LKSection/modules/UserModule/types'
import { ReactSelect } from 'components/ui/Select/ReactSelect'
import { FormItem, Input } from 'components/uiKit'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
import { ALL_EXCEPT_NUMBERS_REGEXP, NOT_LATIN_CYRILLIC_HYPHEN } from 'shared/constants/regExp'
import { BaseOption } from 'shared/types/types'

import { DepartmentCodeFormat, documentNumberFormat, RequisitesCIS } from './requisites'

const FORMAT_DATE = 'DD.MM.YYYY'
export type IdentificationFormProps = {
  uiStore: CompanyDetailsViewModel
}

export const IdentificationForm = observer(({ uiStore }: IdentificationFormProps) => {
  const { t } = useTranslation()

  const getKey = (key: string) => `${UserModuleKeys.passportStep}.${key}`
  const getDataKey = (key: string) => `${UserModuleKeys.data}.${key}`
  const getRequisitesKey = (key: string) => `${UserModuleKeys.requisites}.${key}`
  const localizedGenderOptions = genderOptions.map((gender) => {
    return { ...gender, label: t(getDataKey(gender.label)) }
  })

  const citizenship = uiStore.selectedCitizenship.value

  const isIdentificationNumberVisible = !(
    citizenship === Citizenship.RU ||
    citizenship === Citizenship.KZ ||
    citizenship === Citizenship.AM ||
    citizenship === Citizenship.KG ||
    citizenship === Citizenship.UZ
  )

  const isPersonalNumberVisible = citizenship === Citizenship.UZ || citizenship === Citizenship.KG

  const isPassportIssuedVisible =
    citizenship === Citizenship.KZ || citizenship === Citizenship.GE || citizenship === Citizenship.TJ

  const isDepartmentCodeVisible =
    citizenship !== Citizenship.KZ && citizenship !== Citizenship.TJ && citizenship !== Citizenship.GE

  const stylePicker: React.CSSProperties = { width: 200 }

  const handleSetDateOfIssue = (date: Dayjs, dateString: string | string[]) => {
    if (typeof dateString === 'string') {
      uiStore.setDateOfIssue(dateString)
    }
  }

  const handleSetDateOfBirthday = (date: Dayjs, dateString: string | string[]) => {
    if (typeof dateString === 'string') {
      uiStore.setDateOfBirthday(dateString)
    }
  }

  const handleSetExpirationDate = (date: Dayjs, dateString: string | string[]) => {
    if (typeof dateString === 'string') {
      uiStore.setExpirationDate(dateString)
    }
  }

  return (
    <>
      <FormItem
        label={t(getKey('Номер документа'))}
        name={'documentNumber'}
        rules={[
          { required: true, message: t(getSharedKey('required')) },
          {
            min: documentNumberFormat[citizenship].maxLength,
            message: t(getSharedKey('minLength'), { count: documentNumberFormat[citizenship].maxLength }),
          },
        ]}
        normalize={(value) => {
          if (
            uiStore.selectedCitizenship.value === Citizenship.KZ ||
            uiStore.selectedCitizenship.value === Citizenship.AM
          ) {
            return value?.replace(ALL_EXCEPT_NUMBERS_REGEXP, '')
          } else {
            return value
          }
        }}
      >
        <Input
          maxLength={documentNumberFormat[citizenship].maxLength}
          placeholder={documentNumberFormat[citizenship].placeholder}
          value={uiStore.documentNumber}
          onChange={(e) => uiStore.setDocumentNumber(e.target.value)}
        />
      </FormItem>

      {isIdentificationNumberVisible && (
        <FormItem
          label={t(
            getKey(
              uiStore.selectedCitizenship.value === Citizenship.GE ? 'Персональный номер' : 'Идентификационный номер',
            ),
          )}
          name={'identificationNumber'}
          rules={[
            {
              required: uiStore.selectedCitizenship.value === Citizenship.KZ ? false : true,
              message: t(getSharedKey('required')),
            },
            {
              min: RequisitesCIS[citizenship][DocumentType.identification].maxLength,
              message: t(getSharedKey('minLength'), {
                count: RequisitesCIS[citizenship][DocumentType.identification].maxLength,
              }),
            },
          ]}
          normalize={(value) => {
            if (
              uiStore.selectedCitizenship.value === Citizenship.KZ ||
              uiStore.selectedCitizenship.value === Citizenship.KG ||
              uiStore.selectedCitizenship.value === Citizenship.TJ ||
              uiStore.selectedCitizenship.value === Citizenship.GE
            ) {
              return value?.replace(ALL_EXCEPT_NUMBERS_REGEXP, '')
            } else {
              return value
            }
          }}
        >
          <Input
            maxLength={RequisitesCIS[citizenship][DocumentType.identification].maxLength}
            placeholder={RequisitesCIS[citizenship][DocumentType.identification].placeholder}
            value={uiStore.identificationNumber}
            onChange={(e) => uiStore.setIdentificationNumber(e.target.value)}
          />
        </FormItem>
      )}

      {citizenship === Citizenship.KZ && (
        <>
          <FormItem
            label={t(getKey('ИИН'))}
            name={'iin'}
            rules={[
              { required: true, message: t(getSharedKey('required')) },
              {
                min: 12,
                message: t(getSharedKey('minLength'), {
                  count: 12,
                }),
              },
            ]}
            normalize={(value) => value?.replace(ALL_EXCEPT_NUMBERS_REGEXP, '')}
          >
            <Input
              maxLength={12}
              placeholder={t(getKey('ИИН'))}
              value={uiStore.iin}
              onChange={(e) => uiStore.setIin(e.target.value)}
            />
          </FormItem>
          <FormItem
            label={t(getKey('Национальность'))}
            name={'nationality'}
            rules={[{ required: true, message: t(getSharedKey('required')) }]}
            normalize={(value) => value.replace(NOT_LATIN_CYRILLIC_HYPHEN, '')}
          >
            <Input
              placeholder={t(getKey('Национальность'))}
              value={uiStore.nationality}
              onChange={(e) => uiStore.setNationality(e.target.value)}
            />
          </FormItem>
        </>
      )}

      {isPassportIssuedVisible && (
        <FormItem
          label={t(getKey('Кем выдан'))}
          name={'passportIssued'}
          rules={[{ required: true, message: t(getSharedKey('required')) }]}
        >
          <Input
            placeholder={t(getKey('Кем выдан'))}
            value={uiStore.passportIssued}
            onChange={(e) => uiStore.setPassportIssued(e.target.value)}
          />
        </FormItem>
      )}

      <FormItem
        label={t(getKey('Дата выдачи'))}
        name={'dateOfIssue'}
        rules={[{ required: true, message: t(getSharedKey('required')) }]}
      >
        <DatePicker
          onChange={handleSetDateOfIssue}
          format={FORMAT_DATE}
          value={uiStore.dateOfIssue ? dayjs(uiStore.dateOfIssue, FORMAT_DATE) : null}
          placeholder={'дд.мм.гггг'}
          maxDate={dayjs()}
          style={stylePicker}
        />
      </FormItem>

      <FormItem
        label={t(getKey('Срок действия'))}
        name={'expirationDate'}
        rules={[{ required: true, message: t(getSharedKey('required')) }]}
      >
        <DatePicker
          onChange={handleSetExpirationDate}
          format={FORMAT_DATE}
          value={uiStore.expirationDate ? dayjs(uiStore.expirationDate, FORMAT_DATE) : null}
          placeholder={'дд.мм.гггг'}
          style={stylePicker}
        />
      </FormItem>

      <FormItem
        label={t(getKey('Дата рождения'))}
        name={'dateOfBirthday'}
        rules={[{ required: true, message: t(getSharedKey('required')) }]}
      >
        <DatePicker
          onChange={handleSetDateOfBirthday}
          format={FORMAT_DATE}
          value={uiStore.dateOfBirthday ? dayjs(uiStore.dateOfBirthday, FORMAT_DATE) : null}
          placeholder={'дд.мм.гггг'}
          maxDate={dayjs()}
          style={stylePicker}
        />
      </FormItem>

      {citizenship !== Citizenship.AM && (
        <FormItem
          label={t(getKey('Место рождения'))}
          name={'placeOfBirth'}
          rules={[{ required: true, message: t(getSharedKey('required')) }]}
        >
          <Input
            placeholder={t(getKey('Место рождения'))}
            value={uiStore.placeOfBirth}
            onChange={(e) => uiStore.setPlaceOfBirth(e.target.value)}
          />
        </FormItem>
      )}

      <FormItem label={t(getKey('Пол'))}>
        <ReactSelect
          placeholder={t(getKey('Пол'))}
          options={localizedGenderOptions}
          value={localizedGenderOptions.find((option) => option.value === uiStore.gender)}
          onChange={(item) => {
            const newItem = item as BaseOption<GENDER>
            uiStore.setGender(newItem?.value ?? genderOptions[0].value)
          }}
        />
      </FormItem>

      {isDepartmentCodeVisible && (
        <FormItem
          label={t(getRequisitesKey(DepartmentCodeFormat[citizenship][DocumentType.identification]?.label ?? ''))}
          name={'departmentCode'}
          rules={[
            { required: true, message: t(getSharedKey('required')) },
            {
              min: DepartmentCodeFormat[citizenship][DocumentType.identification]?.maxLength,
              message: t(getSharedKey('minLength'), {
                count: DepartmentCodeFormat[citizenship][DocumentType.identification]?.maxLength,
              }),
            },
          ]}
          normalize={(value) => {
            if (uiStore.selectedCitizenship.value === Citizenship.RU) {
              return value?.replace(/[^0-9+-]/g, '')
            } else if (
              uiStore.selectedCitizenship.value === Citizenship.KG ||
              uiStore.selectedCitizenship.value === Citizenship.UZ
            ) {
              return value
            } else {
              return value?.replace(ALL_EXCEPT_NUMBERS_REGEXP, '')
            }
          }}
        >
          <Input
            placeholder={DepartmentCodeFormat[citizenship][DocumentType.identification]?.placeholder}
            value={uiStore.departmentCode}
            onChange={(e) => uiStore.setDepartmentCode(e.target.value)}
            maxLength={DepartmentCodeFormat[citizenship][DocumentType.identification]?.maxLength}
          />
        </FormItem>
      )}

      {citizenship === Citizenship.AM && (
        <FormItem
          label={t(getKey('Номер социальной карты'))}
          name={'socialCardNumber'}
          rules={[
            { required: true, message: t(getSharedKey('required')) },
            {
              min: 10,
              message: t(getSharedKey('minLength'), {
                count: 10,
              }),
            },
          ]}
          normalize={(value) => value?.replace(ALL_EXCEPT_NUMBERS_REGEXP, '')}
        >
          <Input
            placeholder="1234567890"
            value={uiStore.socialCardNumber}
            onChange={(e) => uiStore.setSocialCardNumber(e.target.value)}
            maxLength={10}
          />
        </FormItem>
      )}

      {isPersonalNumberVisible && (
        <FormItem
          label={t(getKey('Персональный номер'))}
          name={'personalNumber'}
          rules={[
            { required: true, message: t(getSharedKey('required')) },
            {
              min: 14,
              message: t(getSharedKey('minLength'), {
                count: 14,
              }),
            },
          ]}
          normalize={(value) => {
            if (
              uiStore.selectedCitizenship.value === Citizenship.UZ ||
              uiStore.selectedCitizenship.value === Citizenship.KG
            ) {
              return value?.replace(ALL_EXCEPT_NUMBERS_REGEXP, '')
            } else {
              return value
            }
          }}
        >
          <Input
            placeholder="12345678901234"
            value={uiStore.personalNumber}
            onChange={(e) => uiStore.setPersonalNumber(e.target.value)}
            maxLength={14}
          />
        </FormItem>
      )}

      {citizenship === Citizenship.TJ && (
        <FormItem
          label={t(getKey('ИНН гражданина'))}
          name={'innCitizen'}
          rules={[
            { required: true, message: t(getSharedKey('required')) },
            {
              min: 9,
              message: t(getSharedKey('minLength'), {
                count: 9,
              }),
            },
          ]}
          normalize={(value) => {
            if (uiStore.selectedCitizenship.value === Citizenship.TJ) {
              return value.replace(ALL_EXCEPT_NUMBERS_REGEXP, '')
            } else {
              return value
            }
          }}
        >
          <Input
            placeholder="123456789"
            value={uiStore.innCitizen}
            onChange={(e) => uiStore.setInnCitizen(e.target.value)}
            maxLength={9}
          />
        </FormItem>
      )}
    </>
  )
})
